import { z } from 'zod';

import { LiquidationId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { LoanStatus } from './LoanStatus';

/**
 * @deprecated
 * Use LiquidationRecordEntry instead
 * Followup i11086 - migrate to new record type
 */
export const LiquidationRecord = z.object({
  processedDate: zodJSPlainDate,
  soldPrice: z.number(),
  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,
});
export type LiquidationRecord = z.infer<typeof LiquidationRecord>;
/**
 * @deprecated
 */
export const LiquidationRecordInput = LiquidationRecord.pick({
  processedDate: true,
  soldPrice: true,
});
export type LiquidationRecordInput = z.infer<typeof LiquidationRecordInput>;

// --------------------------
const LiquidationStatus = z.enum(['active', 'reversed']);
export type LiquidationStatus = z.infer<typeof LiquidationStatus>;

const LiquidationMethod = z.enum(['shortSale', 'thirdPartySale']);
export type LiquidationMethod = z.infer<typeof LiquidationMethod>;

const LiquidationBalance = z.object({
  balanceAtTimeOfLiquidation: z.number(),
  proceedAmountContributed: z.number().nonnegative(),
});
type LiquidationBalance = z.infer<typeof LiquidationBalance>;

/**
 * Suspense funds applied alongside the sale proceeds,
 * tracked here in case the liquidation is reversed.
 */
export const LiquidationSuspenseApplied = z.object({
  statementSuspense: z.number().nonnegative(),
  futureStatementSuspense: z.number().nonnegative(),
  unappliedSuspense: z.number().nonnegative(),
});
export type LiquidationSuspenseApplied = z.infer<typeof LiquidationSuspenseApplied>;

export const LiquidationRecordEntry = z.object({
  id: LiquidationId,
  status: LiquidationStatus,
  previousLoanStatus: LoanStatus, // for ease of lookup rather than referencing the loan status history

  method: LiquidationMethod,
  dateReceived: zodJSPlainDate, // liquidation date
  saleProceeds: z.number().nonnegative(), // amount available to the lender after the sale
  suspenseApplied: LiquidationSuspenseApplied, // suspense funds applied alongside the sale proceeds
  balances: z.object({
    principal: LiquidationBalance,
    outstandingInterest: LiquidationBalance,
    escrowAdvances: LiquidationBalance,
    corporateAdvances: LiquidationBalance,
    fees: LiquidationBalance,
  }),

  totalPaid: z.number().nonnegative(), // total proceeds (sale proceeds + suspense funds) applied to balances; capped at totalOwed on an overage
  remainingBalanceOwed: z.number(), // a negative remaining balance (aka lender liquidated for overage) will be added to the suspense balance

  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate.optional(),
  reversedAt: zodJSInstantDate.optional(),
});
export type LiquidationRecordEntry = z.infer<typeof LiquidationRecordEntry>;
